import { BehaviorSubject, Observable, filter, first, map } from 'rxjs';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';

import { environment } from '../../../environments/environment';
import { loadIntercom } from '../load-intercom';
import { loadGitlabVisualReview } from '../load-gitlab-visual-review';
import { CaseColumn } from '../../case-management/case-management-cases/cases-filter.service';
import { Column } from '../../case-management/case-management-dashboard/case-management-dashboard.component';

export interface DynamicConfig {
  intercom_id: string;
  display_name: string;
  show_sso: boolean;
  case_columns?: CaseColumn[];
  case_todo_columns: Column[];
  todo_list_marker_filter_fields: string[];
  connect_enabled?: boolean;
  workflow_todo_columns: Column[];
  worklow_configuration_url: string;
  applicant_cases_columns: [];
  report_sharing_enabled: boolean;
  environment_banner_message: string;
  environment_banner_color: string;
  show_environment_banner: boolean;
  debug: boolean;
  employee_directory?: { tab_title: string; columns: { applicant_property: string; label: string }[] };
  service_now_chatbot_enabled?: boolean;
  whatfix_enabled?: boolean;
  use_enhanced_access_controls?: boolean;
  beta_features_enabled: boolean;
  technical_support_email_address: string;
  mixpanel_token?: string;
  portal_url?: string;
  connect_url?: string;
  connect_internal_url?: string;
  waffle: { switches: { [key: string]: { is_active: boolean; last_modified: string } } };
}

@Injectable({
  providedIn: 'root'
})
export class DynamicConfigService {
  private dynamicConfigSubject: BehaviorSubject<DynamicConfig> = new BehaviorSubject<DynamicConfig>(null);
  public dynamicConfig: Observable<DynamicConfig> = this.dynamicConfigSubject
    .asObservable()
    .pipe(filter((dc) => dc !== null));

  constructor(private http: HttpClient, private titleService: Title, @Inject(PLATFORM_ID) private platformId: object) {
    this.http.get<DynamicConfig>(environment.endpoints.dynamicConfig).subscribe((config) => {
      if (config.display_name) {
        this.titleService.setTitle(config.display_name);
      }
      loadIntercom(config.intercom_id, this.platformId, (event) => {
        this.dynamicConfigSubject.next(config);
      });
      this.dynamicConfigSubject.next(config);
    });

    const gitlabConfig = environment['gitlabVisualReview'];
    if (gitlabConfig) {
      loadGitlabVisualReview(gitlabConfig, this.platformId);
    }
  }

  public canAccessBeta(): Observable<boolean> {
    return this.dynamicConfig.pipe(map((config) => config.beta_features_enabled));
  }

  /**
   * Gets the specified field from the dynamic config and only takes the first result.
   * If the specified field doesn't exist, return Observable<null>
   *
   * @param {string} field
   */
  public get(field: string): Observable<any> {
    return this.dynamicConfig.pipe(
      filter((config) => !!config), // Wait until config is loaded to get field
      map((config) => config[field] || null),
      first()
    );
  }

  public getWaffle(field: string): Observable<boolean> {
    return this.dynamicConfig.pipe(
      map((config) => config.waffle.switches[field]?.is_active),
      first()
    );
  }
}
